import { useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import { EXTERNAL_REF_KIND } from "@brainlog/shared";
import { del, errorMessage, post } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function ExternalRefs(props: { ownerType: "note" | "library_item" | "manuscript"; ownerId: string; refs: any[]; readOnly: boolean; onChange(): void }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ kind: "dataset", label: "", url: "", identifier: "", version: "", checksum: "", notes: "" });
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    try {
      await post("/api/external-refs", {
        ownerType: props.ownerType,
        ownerId: props.ownerId,
        kind: form.kind,
        label: form.label,
        url: form.url || null,
        identifier: form.identifier || null,
        version: form.version || null,
        checksum: form.checksum || null,
        notes: form.notes || null,
      });
      setAdding(false);
      setForm({ kind: "dataset", label: "", url: "", identifier: "", version: "", checksum: "", notes: "" });
      props.onChange();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  return (
    <div className="space-y-2 text-sm">
      <p className="text-xs text-muted-foreground">Links to material kept elsewhere. BrainLog does not open or check them.</p>
      <ul className="space-y-1">
        {props.refs.map((r) => (
          <li key={r.id} className="rounded border bg-background px-2 py-1">
            <div className="flex items-start gap-2">
              <span className="shrink-0 text-xs text-muted-foreground">{(EXTERNAL_REF_KIND.labels as Record<string, string>)[r.kind] ?? r.kind}</span>
              <span className="min-w-0 flex-1">
                {r.url ? (
                  <a href={r.url} target="_blank" rel="noopener noreferrer nofollow" className="break-all underline">
                    {r.label}
                  </a>
                ) : (
                  r.label
                )}
                {(r.identifier || r.version || r.checksum) && (
                  <span className="block text-xs text-muted-foreground">
                    {[r.identifier && `ID ${r.identifier}`, r.version && `version ${r.version}`, r.checksum && `checksum (not checked) ${r.checksum}`].filter(Boolean).join(" · ")}
                  </span>
                )}
                {r.notes && <span className="block text-xs text-muted-foreground">{r.notes}</span>}
              </span>
              {!props.readOnly && (
                <button
                  type="button"
                  aria-label={`Remove ${r.label}`}
                  className="rounded p-0.5 hover:bg-accent"
                  onClick={async () => {
                    await del(`/api/external-refs/${r.id}`);
                    props.onChange();
                  }}
                >
                  <X className="size-3.5" />
                </button>
              )}
            </div>
          </li>
        ))}
        {!props.refs.length && <li className="text-muted-foreground">None recorded.</li>}
      </ul>
      {!props.readOnly &&
        (adding ? (
          <form
            className="grid gap-2 rounded border p-2 sm:grid-cols-2"
            onSubmit={(e) => {
              e.preventDefault();
              void save();
            }}
          >
            <div className="space-y-1">
              <Label htmlFor="er-kind">Type</Label>
              <select id="er-kind" className="h-8 w-full rounded-md border bg-background px-2" value={form.kind} onChange={set("kind")}>
                {EXTERNAL_REF_KIND.values.map((k) => (
                  <option key={k} value={k}>
                    {EXTERNAL_REF_KIND.labels[k]}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="er-label">Description</Label>
              <Input id="er-label" className="h-8" value={form.label} onChange={set("label")} required maxLength={300} />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label htmlFor="er-url">Link (https://…, doi:…, git@…)</Label>
              <Input id="er-url" className="h-8" value={form.url} onChange={set("url")} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="er-id">Identifier</Label>
              <Input id="er-id" className="h-8" value={form.identifier} onChange={set("identifier")} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="er-ver">Version</Label>
              <Input id="er-ver" className="h-8" value={form.version} onChange={set("version")} />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label htmlFor="er-sum">Checksum (optional, not checked)</Label>
              <Input id="er-sum" className="h-8" value={form.checksum} onChange={set("checksum")} />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label htmlFor="er-notes">Notes</Label>
              <Input id="er-notes" className="h-8" value={form.notes} onChange={set("notes")} />
            </div>
            <div className="flex gap-2 sm:col-span-2">
              <Button type="submit" size="sm" disabled={!form.label.trim()}>
                Save reference
              </Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => setAdding(false)}>
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <Button size="xs" variant="outline" onClick={() => setAdding(true)}>
            Add external reference…
          </Button>
        ))}
    </div>
  );
}
